import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { AppButton, AppTextField } from "../../../components/form";
import { PageSectionCard } from "../../../components/layout";
import {
  platformAdminService,
  type PlatformLlmModel,
  type PlatformLlmModelPayload,
} from "../../../services/platformAdminService";

type FormState = {
  id?: string;
  name: string;
  modelId: string;
  baseUrl: string;
  apiKey: string;
  beasResourceEnabled: boolean;
  enabled: boolean;
  sortOrder: number;
};

const emptyForm = (): FormState => ({
  name: "",
  modelId: "",
  baseUrl: "https://integrate.api.nvidia.com/v1",
  apiKey: "",
  beasResourceEnabled: true,
  enabled: true,
  sortOrder: 0,
});

function toForm(row: PlatformLlmModel): FormState {
  return {
    id: row.id,
    name: row.name || "",
    modelId: row.modelId || "",
    baseUrl: row.baseUrl || "",
    apiKey: "",
    beasResourceEnabled: row.beasResourceEnabled !== false,
    enabled: row.enabled !== false,
    sortOrder: row.sortOrder ?? 0,
  };
}

export default function BeasLlmModelsPanel() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rows, setRows] = useState<PlatformLlmModel[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await platformAdminService.listLlmModels();
      setRows(Array.isArray(list) ? list : []);
    } catch {
      setError(t("superAdmin.llmModels.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const payload = (): PlatformLlmModelPayload => ({
    name: form.name.trim(),
    modelId: form.modelId.trim(),
    baseUrl: form.baseUrl.trim(),
    apiKey: form.apiKey.trim() || undefined,
    provider: "OPENAI_COMPATIBLE",
    beasResourceEnabled: form.beasResourceEnabled,
    enabled: form.enabled,
    sortOrder: form.sortOrder,
  });

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (form.id) {
        await platformAdminService.updateLlmModel(form.id, payload());
      } else {
        await platformAdminService.createLlmModel(payload());
      }
      setForm(emptyForm());
      setSuccess(t("superAdmin.llmModels.saveSuccess"));
      await load();
    } catch {
      setError(t("superAdmin.llmModels.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!form.id && !form.apiKey.trim()) {
      setError(t("superAdmin.llmModels.testNeedsKey"));
      return;
    }
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      if (form.id) {
        await platformAdminService.testLlmModel(form.id, payload());
      } else {
        // Create temporarily is not needed — require save first for new rows without id.
        setError(t("superAdmin.llmModels.saveBeforeTest"));
        return;
      }
      setSuccess(t("superAdmin.llmModels.testSuccess"));
    } catch {
      setError(t("superAdmin.llmModels.testError"));
    } finally {
      setTesting(false);
    }
  };

  const onDelete = async (id: string) => {
    setError(null);
    setSuccess(null);
    try {
      await platformAdminService.deleteLlmModel(id);
      if (form.id === id) setForm(emptyForm());
      setSuccess(t("superAdmin.llmModels.deleteSuccess"));
      await load();
    } catch {
      setError(t("superAdmin.llmModels.deleteError"));
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <PageSectionCard
        title={t("superAdmin.llmModels.title")}
        description={t("superAdmin.llmModels.description")}
      >
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
          <Typography variant="body2" color="text.secondary">
            {t("superAdmin.llmModels.hint")}
          </Typography>
          {rows.length === 0 ? (
            <Alert severity="info">{t("superAdmin.llmModels.empty")}</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("superAdmin.llmModels.name")}</TableCell>
                  <TableCell>{t("superAdmin.llmModels.modelId")}</TableCell>
                  <TableCell>{t("superAdmin.llmModels.flags")}</TableCell>
                  <TableCell align="right">{t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover selected={form.id === row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {row.modelId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {[
                        row.enabled ? t("superAdmin.llmModels.enabledShort") : t("superAdmin.llmModels.disabledShort"),
                        row.beasResourceEnabled ? "BEAS" : "—",
                        row.apiKeyConfigured ? "key" : "no-key",
                      ].join(" · ")}
                    </TableCell>
                    <TableCell align="right">
                      <AppButton size="small" variant="text" onClick={() => setForm(toForm(row))}>
                        {t("common.edit")}
                      </AppButton>
                      <AppButton size="small" variant="text" color="error" onClick={() => void onDelete(row.id)}>
                        {t("common.delete")}
                      </AppButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      </PageSectionCard>

      <PageSectionCard
        title={form.id ? t("superAdmin.llmModels.editTitle") : t("superAdmin.llmModels.createTitle")}
      >
        <Stack spacing={2} maxWidth={640}>
          <AppTextField
            label={t("superAdmin.llmModels.name")}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
          />
          <AppTextField
            label={t("superAdmin.llmModels.modelId")}
            value={form.modelId}
            onChange={(e) => setForm((f) => ({ ...f, modelId: e.target.value }))}
            fullWidth
            helperText={t("superAdmin.llmModels.modelIdHelp")}
          />
          <AppTextField
            label={t("superAdmin.llmModels.baseUrl")}
            value={form.baseUrl}
            onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
            fullWidth
          />
          <AppTextField
            label={
              form.id
                ? t("superAdmin.llmModels.apiKeyKeep")
                : t("superAdmin.llmModels.apiKey")
            }
            type="password"
            value={form.apiKey}
            onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
            fullWidth
            autoComplete="new-password"
          />
          <AppTextField
            label={t("superAdmin.llmModels.sortOrder")}
            type="number"
            value={String(form.sortOrder)}
            onChange={(e) =>
              setForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 0 }))
            }
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.beasResourceEnabled}
                onChange={(_, checked) => setForm((f) => ({ ...f, beasResourceEnabled: checked }))}
              />
            }
            label={t("superAdmin.llmModels.beasResourceEnabled")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.enabled}
                onChange={(_, checked) => setForm((f) => ({ ...f, enabled: checked }))}
              />
            }
            label={t("superAdmin.llmModels.enabled")}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <AppButton variant="outlined" onClick={() => setForm(emptyForm())} disabled={saving || testing}>
              {t("common.cancel")}
            </AppButton>
            <AppButton variant="outlined" onClick={() => void onTest()} disabled={testing || saving || !form.id}>
              {testing ? t("superAdmin.llmModels.testing") : t("superAdmin.llmModels.test")}
            </AppButton>
            <AppButton onClick={() => void onSave()} disabled={saving || testing}>
              {saving ? t("common.saving") : t("common.save")}
            </AppButton>
          </Stack>
        </Stack>
      </PageSectionCard>
    </Stack>
  );
}
